import { DialogOverlay as DialogOverlay$1, DialogContent as DialogContent$1 } from '@reach/dialog';
import React__default, { useContext, useRef, useLayoutEffect } from 'react';
import { animated, useTransition } from 'react-spring';
import styled, { css, useTheme, ThemeProvider } from 'styled-components';
import { useKeyPress } from '../../lib/keypress.js';
import { useQueryChildComponents, HappyWebSystemError } from '../base/SystemComponent.js';
import { ButtonIcon } from '../button/ButtonIcon.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { Heading } from '../heading/Heading.js';
import { Box } from '../layout/Box.js';
import { Inline } from '../layout/Inline.js';
import { Stack } from '../layout/Stack.js';
import { Menu, MenuButtonIcon } from '../menu/Menu.js';
import { CSSOpacityHex, CSSResponsiveQuery } from '../styles/cssGenerate.js';
import { getColor, setPortalLayer } from '../styles/stylePropTypes.js';

const ModalCheckerContext = React__default.createContext(undefined);
const CheckModalProvider = (props)=>{
    return React__default.createElement(ModalCheckerContext.Provider, {
        value: true
    }, props.children);
};
/**
 * @deprecated Modal layering for popovers should be handled via token layering
 */ const useCheckModal = ()=>{
    const context = useContext(ModalCheckerContext);
    return !!context;
};
const DialogOverlay = animated(styled(DialogOverlay$1)`
  &[data-reach-dialog-overlay] {
    display: flex;
    background: ${(props)=>getColor("contextDark60", props.theme)}${CSSOpacityHex(0.5)};
    display: flex;
    flex-direction: column;
    justify-content: center;
    ${setPortalLayer()}
  }
`);
const DialogContent = animated(styled(DialogContent$1)`
  &[data-reach-dialog-content] {
    width: 100%;
    border-radius: 1.5rem;
    background: ${COLOR_PALETTE.white};
    position: relative;

    padding: 0;
    overflow: hidden;
    max-height: 90vh;
    max-width: 50rem;

    ${CSSResponsiveQuery("mobile")(css`
      max-height: 80vh;
      max-width: 90vw;
    `)}

    ${(props)=>props.$type === "side" && css`
        position: absolute;
        top: 3.5rem;
        bottom: 0;
        right: 0;
        margin: 0;

        height: calc(100vh - 3.5rem);
        width: 34rem;
        max-width: 100vw;
        max-height: 100vh;

        border-radius: 1.5rem 0 0 1.5rem;
        box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
      `}
    ${(props)=>props.$type === "fullscreen" && css`
        height: 100vh;
        width: 100vw;
        max-width: 100vw;
        max-height: 100vh;
        margin: 0;
        border-radius: 0;
      `}
  }

  &[data-reach-dialog-content] [data-hpy-web-modal-action-buttons] [${ButtonIcon.dataId}] {
    font-size: 0.75rem;
    width: 1.5rem;
    height: 1.5rem;
    color: ${COLOR_PALETTE.slateGrey40};
    z-index: 1;
    line-height: 1;

    &:focus {
      outline: none;
    }

    &:hover {
      color: ${COLOR_PALETTE.slateGrey100};
    }
  }
`);
const ModalActions = ({ children , ...props })=>{
    return React__default.createElement(React__default.Fragment, null, children);
};
ModalActions.dataId = "data-hpy-web-modal-actions";
ModalActions.displayName = "ModalActions";
const ModalHeader = ({ children , ...props })=>{
    const [actions, heading] = useQueryChildComponents(children, [
        ModalActions
    ]);
    if (heading.length > 0 && typeof heading[0] !== "string") {
        throw new HappyWebSystemError("ModalHeader", 'Invalid child component type - only "string" or ModalActions are supported as children.');
    }
    return React__default.createElement(Box, {
        "data-hpy-web-modal-header": true,
        ...props,
        padding: [
            3,
            3,
            0,
            3
        ],
        width: "fill"
    }, React__default.createElement(Inline, {
        gap: 0.5,
        alignY: "center",
        width: "fill"
    }, React__default.createElement(Box, {
        flexGrow: 1
    }, React__default.createElement(Heading, {
        level: "3"
    }, heading)), React__default.createElement(Inline, {
        gap: 0.5,
        align: "right",
        alignY: "center"
    }, actions)));
};
ModalHeader.dataId = "data-hpy-web-modal-header";
ModalHeader.displayName = "ModalHeader";
const ModalContent = ({ children , ...props })=>{
    return React__default.createElement(Box, {
        "data-hpy-web-modal-content": true,
        ...props,
        padding: [
            3
        ],
        flexGrow: 1,
        overflow: "scroll",
        height: "fill"
    }, children);
};
ModalContent.dataId = "data-hpy-web-modal-content";
ModalContent.displayName = "ModalContent";
const StyledFooterBox = styled(Box)`
  justify-content: space-between;
`;
const ModalFooter = ({ children , ...props })=>{
    return React__default.createElement(StyledFooterBox, {
        "data-hpy-web-modal-footer": true,
        ...props,
        padding: [
            0,
            3,
            3,
            3
        ]
    }, children);
};
ModalFooter.dataId = "data-hpy-web-modal-footer";
ModalFooter.displayName = "ModalFooter";
/**
 * On first render of the Modal, set automatic focus of interactable children
 */ const AutoFocus = ({ children  })=>{
    const ref = useRef();
    useLayoutEffect(()=>{
        if (ref.current !== undefined) {
            // Preference auto focus on form elements within modal
            const inputs = ref.current.querySelectorAll('input, select, textarea, [tabindex]:not([tabindex="-1"])');
            if (inputs.length > 0) {
                inputs[0]?.focus();
                return;
            }
            // If no form elements, auto focus on any buttons within modal (e.g. close button)
            const interactables = ref.current.querySelectorAll("button, [href]");
            if (interactables.length > 0) {
                interactables[0]?.focus();
                return;
            }
        }
    }, []);
    return React__default.createElement(Box, {
        ref: ref,
        height: "fill"
    }, children);
};
/**
 * Modal.
 * Provides composable Modal functionality.
 *
 * @example
 * // manually control modal open/closed state
 * // (recommended for dialogs)
 * <Modal isOpen={showModal} onDismiss={close}>
 *  ...
 * </Modal>
 */ const Modal = ({ type ="dialog" , isOpen =false , testId ="X" , children , ...props })=>{
    const theme = useTheme();
    useKeyPress("Escape", props.onDismiss);
    const offsetX = type === "side" ? 34 : 5;
    const transition = useTransition(isOpen, {
        from: {
            opacity: 0,
            offset: offsetX
        },
        enter: {
            opacity: 1,
            offset: 0
        },
        leave: {
            opacity: 0,
            offset: offsetX
        },
        config: {
            mass: 1,
            tension: 210,
            friction: 20,
            clamp: type !== "dialog"
        }
    });
    return transition((styles, show)=>show && React__default.createElement(DialogOverlay, {
            "data-hpy-web-modal": true,
            ...props,
            style: {
                ...props?.style,
                opacity: styles.opacity
            },
            dangerouslyBypassFocusLock: true
        }, React__default.createElement(ThemeProvider, {
            theme: {
                ...theme,
                PORTAL_LAYER: theme?.PORTAL_LAYER ? theme?.PORTAL_LAYER + 1 : 1
            }
        }, React__default.createElement(CheckModalProvider, null, React__default.createElement(DialogContent, {
            "data-test-id": "Modal-" + testId,
            style: {
                transform: styles.offset.to((value)=>type === "side" ? `translateX(${value}rem)` : `translateY(${value}rem)`)
            },
            "data-hpy-web-modal-content": true,
            "$type": type
        }, React__default.createElement(AutoFocus, null, React__default.createElement(Stack, {
            height: "fill"
        }, children)))))));
};
Modal.dataId = "data-hpy-web-modal";
Modal.displayName = "Modal";
const ModalCloseButton = ({ testId ="X" , ...props })=>{
    return React__default.createElement(ButtonIcon, {
        "data-hpy-web-modal-close-button": true,
        icon: "close",
        ...props,
        testId: "ModalClose-" + testId
    });
};
ModalCloseButton.dataId = "data-hpy-web-modal-close-button";
ModalCloseButton.displayName = "ModalCloseButton";
const ModalActionMenu = ({ testId ="X" , children , ...props })=>{
    return React__default.createElement(Menu, {
        "data-hpy-web-modal-action-menu": true,
        ...props,
        testId: "ModalActionMenu-" + testId
    }, React__default.createElement(MenuButtonIcon, {
        icon: "overflow",
        testId: "ModalActionMenu-" + testId
    }), children);
};
ModalActionMenu.dataId = "data-hpy-web-modal-action-menu";
ModalActionMenu.displayName = "ModalActionMenu";

export { Modal, ModalActionMenu, ModalActions, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, useCheckModal };
